import { z } from 'zod'
import { NON_EU_COUNTRIES_WITHOUT_POSTCODES } from '../config/countries'
import {
  createRequiredLatinCharacterSchema,
  createOptionalLatinCharacterSchema
} from '../utils/latinCharacterValidation'

const baseLatinCharacterAddressSchema = z.object({
  country: z.string().length(2).toLowerCase(),
  street: createOptionalLatinCharacterSchema('Street address must use Latin characters', {
    max: 70
  }),
  street2: createOptionalLatinCharacterSchema('Street address must use Latin characters', {
    max: 70
  }),
  streetNumber: createOptionalLatinCharacterSchema('Street number must use Latin characters', {
    max: 35
  }),
  subDivision: createOptionalLatinCharacterSchema('Subdivision must use Latin characters'),
  postCode: createOptionalLatinCharacterSchema('Post code must use Latin characters', { max: 17 }),
  poBox: createOptionalLatinCharacterSchema('P.O. Box must use Latin characters', { max: 70 }),
  city: createRequiredLatinCharacterSchema('City must use Latin characters', { min: 2, max: 35 })
})

export const addressSchema = baseLatinCharacterAddressSchema.refine(
  data => {
    const doesCountryHavePostCode = !NON_EU_COUNTRIES_WITHOUT_POSTCODES.includes(data.country)

    if (doesCountryHavePostCode && !data.postCode) {
      return false
    }
    return true
  },
  {
    message: 'Post code is required for countries with post codes',
    path: ['postCode']
  }
)

export const updateAddressSchema = baseLatinCharacterAddressSchema.partial().refine(
  data => {
    if (!data.country) {
      return false
    }

    const doesCountryHavePostCode = !NON_EU_COUNTRIES_WITHOUT_POSTCODES.includes(data.country)

    if (doesCountryHavePostCode && !data.postCode) {
      return false
    }
    return true
  },
  {
    message: 'Post code is required for countries with post codes',
    path: ['postCode']
  }
)

export const localAddressSchema = z
  .object({
    localStreet: z.string().max(70).optional(),
    localStreet2: z.string().max(70).optional(),
    localStreetNumber: z.string().max(35).optional(),
    localSubDivision: z.string().optional(),
    country: z.string().length(2).toLowerCase(),
    localPostCode: z.string().max(17).optional(),
    localPoBox: z.string().max(70).optional(),
    localCity: z.string().min(2).max(35)
  })
  .refine(
    data => {
      const doesCountryHavePostCode = !NON_EU_COUNTRIES_WITHOUT_POSTCODES.includes(data.country)

      if (doesCountryHavePostCode && !data.localPostCode) {
        return false
      }
      return true
    },
    {
      message: 'Post code is required for countries with post codes',
      path: ['localPostCode']
    }
  )

export const declarantAddressSchema = z.object({
  street: z.string().max(70).optional().or(z.literal('')),
  street2: z.string().max(70).optional().or(z.literal('')),
  streetNumber: z.string().max(35).optional().or(z.literal('')),
  subDivision: z.string().optional().or(z.literal('')),
  country: z.string().length(2).toLowerCase(),
  postCode: z.string().max(17).optional().or(z.literal('')),
  poBox: z.string().max(70).optional().or(z.literal('')),
  city: z.string().min(2).max(35)
})

export type Address = z.infer<typeof addressSchema>
export type LocalAddress = z.infer<typeof localAddressSchema>
